// services/api.ts
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';

console.log(`🔧 API Base URL: ${API_BASE_URL} (Mode: ${import.meta.env.MODE})`);

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // Increased timeout
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    try {
      // Get token from localStorage or sessionStorage directly
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('⚠️ No auth token found');
      }

      // Debug logging
      console.log('🔍 API Request:', {
        url: config.url,
        method: config.method,
        hasToken: !!token,
        headers: config.headers,
      });

    } catch (error) {
      console.error('❌ Request interceptor error:', error);
    }

    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', {
      url: response.config.url,
      status: response.status,
      data: response.data?.success !== undefined ? response.data : 'data received'
    });
    return response;
  },
  async (error) => {
    console.error('❌ API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data
    });

    const status = error.response?.status;
    const url = error.config?.url || '';

    // Don't logout for 404 errors on compliance checklist endpoints
    if (status === 404 && url.includes('/compliance-checklist')) {
      console.log('📋 Compliance checklist endpoint not implemented yet - ignoring');
      // Return a mock response to prevent the error from propagating
      return Promise.resolve({
        data: {
          success: true,
          data: {
            checklist: {}
          }
        }
      });
    }

    // Don't logout for 404 errors on any endpoint (resource not found)
    if (status === 404) {
      console.log('🔍 Resource not found - not logging out');
      return Promise.reject(error);
    }

    // Don't logout for 403 errors that are specifically about permissions
    // Only logout for token expiration issues
    if (status === 403) {
      const errorMessage = error.response?.data?.message || '';

      // Check if it's a permission issue vs token expiration
      if (errorMessage.includes('token') || errorMessage.includes('expired') || errorMessage.includes('invalid')) {
        console.log('🔒 Token expired or invalid - logging out');

        // Clear all auth data
        const authStore = useAuthStore();
        authStore.clearAuth();

        // Redirect to login if we're in browser
        if (typeof window !== 'undefined') {
          window.location.href = '/';
        }
      } else {
        console.log('🔒 Permission denied - not logging out');
        // Just show a notification instead of logging out
        // You might want to use a notification service here
      }
    }

    // Handle 401 (unauthorized) - Always logout for 401
    if (status === 401) {
      console.log('🔒 Unauthorized - logging out');
      const authStore = useAuthStore();
      authStore.clearAuth();

      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
